import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import {
  api,
  persistAuth,
  clearAuth,
  getStoredUser,
  getStoredToken,
  type AuthResponse,
  type AuthUser,
  type LoginPayload,
  type RegisterPayload,
} from '../../utils/api';

export type AuthStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  status: AuthStatus;
  error: string | null;
}

const initialState: AuthState = {
  user: getStoredUser(),
  token: getStoredToken(),
  status: 'idle',
  error: null,
};

const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    return (
      (typeof error.response?.data === 'object' && (error.response?.data as { message?: string })?.message) ||
      error.message ||
      'Something went wrong.'
    );
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
};

export const loginUser = createAsyncThunk<AuthResponse, LoginPayload, { rejectValue: string }>(
  'auth/loginUser',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post<AuthResponse>('/auth/login', payload);
      persistAuth(response.data);
      const firstName = response.data.user.name.split(' ')[0] ?? 'there';
      toast.success(`Welcome back, ${firstName}!`);
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const registerUser = createAsyncThunk<AuthResponse, RegisterPayload, { rejectValue: string }>(
  'auth/registerUser',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post<AuthResponse>('/auth/register', payload);
      persistAuth(response.data);
      toast.success('Account created successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.status = 'idle';
      state.error = null;
      clearAuth();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Unable to log in.';
      })
      .addCase(registerUser.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Unable to register.';
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
